import { inject, Injectable, OnDestroy } from '@angular/core';
import { concatMap, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { CucAutoAttendantsReport, JobState, JobStatus } from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { filter, map, take, tap } from 'rxjs/operators';
import { MigrationsCucAutoAttendantsPollingContext } from './migrations-cuc-auto-attendants-polling.context';

@Injectable()
export class MigrationsCucAutoAttendantsContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _migrationsCucAutoAttendantsPollingContext = inject(MigrationsCucAutoAttendantsPollingContext);

  private _stateSource = new ReplaySubject<CucAutoAttendantsReport>(1);
  state$ = this._stateSource.asObservable();

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/cisco/${this._route.snapshot.params.id}/auto-attendants-report`;

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._migrationsCucAutoAttendantsPollingContext.stopPolling();
  }

  refresh(): Observable<void> {
    return this._refresh().pipe(switchMap(() => this._init()));
  }

  getAudioFileUrl(autoAttendantId: string, filename: string): string {
    return `${this._baseUrl}/${encodeURIComponent(autoAttendantId)}/audioFiles/${encodeURIComponent(filename)}`;
  }

  getAudioFileAsBlob(autoAttendantId: string, filename: string): Observable<Blob> {
    const url = `${this._baseUrl}/${encodeURIComponent(autoAttendantId)}/audioFiles/${encodeURIComponent(filename)}`;
    return this._http.get(url, { responseType: 'blob' });
  }

  private _init(): Observable<void> {
    this._migrationsCucAutoAttendantsPollingContext.startPolling();
    return this._migrationsCucAutoAttendantsPollingContext.state$.pipe(
      tap((status) => this._jobStateSource.next(status)),
      filter((status) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._refresh();
        }
        this._migrationsCucAutoAttendantsPollingContext.stopPolling();
        if (!status.error) {
          return this._get();
        }
        return of(null);
      }),
      filter((report) => !!report),
      map((report: CucAutoAttendantsReport) => {
        this._stateSource.next(report);
      }),
      take(1),
    );
  }

  private _get(): Observable<CucAutoAttendantsReport> {
    return this._http.get<CucAutoAttendantsReport>(this._baseUrl);
  }

  private _refresh(): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/refresh`, null);
  }
}
